import { AxisCode, ClassifiedObjectType, type ObjectTag } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Selection } from '@/domain/work-order.types'
import type { RailAxis } from '@/hooks/useLibrary'
import { WORK_ORDER_MESSAGES_KEY } from '@/hooks/useWorkOrderMessages'
import { type ClassifyTuple, classificationApi } from '@/services/classification.service'

/** A picker selection turned into one classify tuple per (axis, value) for one message. */
export const toMessageTuples = (messageId: string, classification: Selection): ClassifyTuple[] =>
	Object.entries(classification).flatMap(([axisCode, valueCodes]) =>
		(valueCodes ?? []).map((valueCode) => ({
			objectType: ClassifiedObjectType.MESSAGE,
			objectId: messageId,
			axisCode,
			valueCode,
		})),
	)

/**
 * Split a message's resolved tags by whether their axis is a real registry DIMENSION
 * axis (present in the filter-rail `axes` list). Dimension tags become an editable
 * picker selection (`{ axisCode: codes[] }`); the rest (free-text attributes the
 * picker can't resolve to options) become chips displayed with their resolved label.
 */
export const partitionTags = (
	tags: ObjectTag[],
	axes: RailAxis[],
): { selection: Selection; attributes: ObjectTag[] } => {
	const dimensionCodes = new Set(axes.map((a) => a.code))
	const selection: Selection = {}
	const attributes: ObjectTag[] = []
	for (const t of tags) {
		if (dimensionCodes.has(t.axisCode)) (selection[t.axisCode] ??= []).push(t.valueCode)
		else attributes.push(t)
	}
	return { selection, attributes }
}

/** A message's resolved dimension classification tags (codes + labels). */
export const useMessageClassification = (id: string | null) =>
	useQuery({
		queryKey: ['message-classification', id],
		queryFn: async () => {
			const [obj] = await classificationApi.getObjectTags(ClassifiedObjectType.MESSAGE, [id as string])
			return obj?.tags ?? []
		},
		enabled: !!id,
	})

/** Attribute axes surfaced for a message (imported messages carry `order_code`). */
export const MESSAGE_ATTRIBUTE_AXES: AxisCode[] = [AxisCode.ORDER_CODE]

/** One read-only attribute chip: an axisCode + a single value CODE (attributes have no labels). */
export type AttributeChip = { axisCode: string; value: string }

/**
 * A message's `object_attribute` values (e.g. order_code), flattened to one chip per
 * value. Codes ARE the value for free-text attributes — no label resolution exists.
 */
export const useMessageAttributes = (id: string | null) =>
	useQuery({
		queryKey: ['message-attributes', id],
		queryFn: async (): Promise<AttributeChip[]> => {
			const [obj] = await classificationApi.getObjectAttributes(
				ClassifiedObjectType.MESSAGE,
				[id as string],
				MESSAGE_ATTRIBUTE_AXES,
			)
			return Object.entries(obj?.attributes ?? {}).flatMap(([axisCode, values]) =>
				values.map((value) => ({ axisCode, value })),
			)
		},
		enabled: !!id,
	})

/**
 * Replace the message's `order_code` attribute with the form's set — the field shows
 * every order the message is on, so a save asserts the whole set rather than adding to it.
 *
 * Both the order the message joins AND the one it leaves must re-read, so the whole
 * `work-order-messages` family is invalidated rather than one order's key. Errors are
 * reported by the calling page, which sequences this with the classification write and
 * has to report either failing the same way.
 */
export const useSetMessageOrderCodes = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ messageId, orderCodes }: { messageId: string; orderCodes: string[] }) =>
			classificationApi.setAttribute(ClassifiedObjectType.MESSAGE, messageId, AxisCode.ORDER_CODE, orderCodes),
		onSuccess: (_result, { messageId }) => {
			void qc.invalidateQueries({ queryKey: ['message-attributes', messageId] })
			void qc.invalidateQueries({ queryKey: [WORK_ORDER_MESSAGES_KEY] })
		},
	})
}
